import React, { useCallback, useEffect, useState } from 'react';
import { TooltipWrapper, TooltipBubble, TooltipDismiss, UnifiedFAB, FabBtn, FabDivider, FabBadge } from './styles';
import WidgetContainer from './WidgetContainer';
import { useChatWidget } from './hooks/useChatWidget';
import { useVoiceCall } from '../../contexts/VoiceCallContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface ChatWidgetProps {
  isDarkMode: boolean;
}

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ChatWidget: React.FC<ChatWidgetProps> = ({ isDarkMode: initialDarkMode }) => {
  const [isDarkMode, setIsDarkMode] = useState(initialDarkMode);
  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  const {
    isExpanded,
    isSidebarOpen,
    isMaximized,
    showTooltip,
    unreadCount,
    openWidget,
    closeWidget,
    toggleSidebar,
    toggleMaximize,
    dismissTooltip,
    incrementUnreadCount,
  } = useChatWidget();

  const { status: callStatus, startCall } = useVoiceCall();
  const { t } = useLanguage();

  const isCallActive = callStatus !== 'idle';

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) closeWidget();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded, closeWidget]);

  const handleMessageReceived = useCallback(() => {
    if (!isExpanded) incrementUnreadCount();
  }, [isExpanded, incrementUnreadCount]);

  return (
    <>
      <UnifiedFAB isHidden={isExpanded}>
        <FabBtn
          onClick={isCallActive ? undefined : startCall}
          isActive={isCallActive}
          aria-label={t('voice.call.aria.start')}
          title={isCallActive ? t('voice.call.in_call') : t('voice.call.start')}
        >
          <MicIcon />
        </FabBtn>

        <FabDivider />

        <FabBtn
          onClick={openWidget}
          aria-label={t('widget.openChat')}
          title={t('widget.openChat')}
        >
          <ChatIcon />
          {unreadCount > 0 && (
            <FabBadge>{unreadCount > 9 ? '9+' : unreadCount}</FabBadge>
          )}
        </FabBtn>
      </UnifiedFAB>

      <TooltipWrapper visible={showTooltip && !isExpanded}>
        <TooltipBubble onClick={() => { dismissTooltip(); openWidget(); }}>
          <strong>👋 Hi there!</strong>
          Ask me anything about Yuanyuan
          <TooltipDismiss onClick={e => { e.stopPropagation(); dismissTooltip(); }}>✕</TooltipDismiss>
        </TooltipBubble>
      </TooltipWrapper>

      <WidgetContainer
        isExpanded={isExpanded}
        isSidebarOpen={isSidebarOpen}
        isMaximized={isMaximized}
        isDarkMode={isDarkMode}
        onToggleSidebar={toggleSidebar}
        onToggleMaximize={toggleMaximize}
        onToggleDarkMode={toggleDarkMode}
        onClose={closeWidget}
        onMessageReceived={handleMessageReceived}
      />
    </>
  );
};

export default ChatWidget;
